/**
 * Automatisch doorladen bij scrollen, en beelden pas ophalen als ze in de buurt
 * komen (30-09-2026, Jordy: "Load more moet vanzelf, geen knop").
 *
 * Waarom een eigen IntersectionObserver en niet alleen `loading="lazy"`: de
 * browser rekent zijn laadmarge vanaf het hele venster, niet vanaf het
 * scrollvak waar het raster in staat. Gemeten in de contentkiezer: 12 kaarten
 * in beeld, 44 van de 48 miniaturen meteen opgehaald. Met het scrollvak als
 * `root` laadt alleen wat er echt bijna in beeld is.
 */
import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

/**
 * Roept `opRand` aan zodra `wacht` (een leeg elementje aan de rand van de lijst)
 * binnen `marge` van het zichtbare deel van `root` komt. `aan` = false zet het
 * uit (bijvoorbeeld als er niets meer te laden is of er al iets onderweg is).
 */
export function useBijRand(
  wacht: RefObject<Element | null>,
  root: RefObject<Element | null>,
  opRand: () => void,
  aan: boolean,
  marge = '400px',
): void {
  const laatste = useRef(opRand)
  useEffect(() => {
    laatste.current = opRand
  }, [opRand])
  useEffect(() => {
    const el = wacht.current
    if (!aan || !el || typeof IntersectionObserver === 'undefined') return
    const waarnemer = new IntersectionObserver(
      (items) => {
        if (items.some((i) => i.isIntersecting)) laatste.current()
      },
      { root: root.current, rootMargin: marge },
    )
    waarnemer.observe(el)
    return () => waarnemer.disconnect()
  }, [wacht, root, aan, marge])
}

/**
 * True zodra `el` binnen `marge` van het zichtbare deel van `root` komt; blijft
 * daarna true (een eenmaal geladen beeld hoeft niet weer weg).
 */
export function useInBeeld(
  el: RefObject<Element | null>,
  root: RefObject<Element | null>,
  marge = '200px',
): boolean {
  const [zichtbaar, setZichtbaar] = useState(false)
  useEffect(() => {
    const doel = el.current
    if (zichtbaar || !doel) return
    if (typeof IntersectionObserver === 'undefined') {
      setZichtbaar(true)
      return
    }
    const waarnemer = new IntersectionObserver(
      (items) => {
        if (items.some((i) => i.isIntersecting)) {
          setZichtbaar(true)
          waarnemer.disconnect()
        }
      },
      { root: root.current, rootMargin: marge },
    )
    waarnemer.observe(doel)
    return () => waarnemer.disconnect()
  }, [el, root, marge, zichtbaar])
  return zichtbaar
}

/**
 * Haalt een reeks beeld-URL's alvast op, met hooguit `tegelijk` tegelijk, zodat
 * ze in de browsercache staan als ze in beeld komen — en de server de
 * miniatuur al gemaakt heeft. Stopt als `stop()` true geeft (andere filter).
 */
export async function warmBeelden(
  urls: string[],
  tegelijk: number,
  stop: () => boolean,
): Promise<void> {
  let i = 0
  const werker = async () => {
    while (i < urls.length && !stop()) {
      const url = urls[i++]
      await new Promise<void>((klaar) => {
        const img = new Image()
        img.onload = () => klaar()
        img.onerror = () => klaar()
        img.src = url
      })
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, tegelijk) }, werker))
}
